import { FontColorEnum, FontSizeEnum, Typography } from '@acceso/design-system';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { OptionsFooter } from '@/features/mentions/components/MentionFilters/active-options/OptionsFooter';
import { CataloguePicker } from '@/hooks/use-catalogue-picker';
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel';

export type CatalogueListboxProps = {
  isDark?: boolean;
  mode: 'single' | 'multiple';
  /** The selection; single mode shows none, as the inline dropdowns only report a choice. */
  value?: string[];
  onChange: (next: ListboxSelected) => void;
  onClose?: () => void;
};

type Props<TEntry> = CatalogueListboxProps & {
  picker: CataloguePicker<TEntry>;
  loadingMoreLabel: string;
  footerTestId: string;
};

/**
 * A `Listbox` over a catalogue picker. Mounted only while its dropdown is open, so the typed term
 * dies with it, and the sentinel's hook lives in the same tree as the sentinel it observes.
 */
export function CatalogueListbox<TEntry>({
  isDark,
  mode,
  value,
  onChange,
  onClose,
  picker,
  loadingMoreLabel,
  footerTestId,
}: Props<TEntry>) {
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: picker.hasNextPage,
    isFetchingNextPage: picker.isLoadingMore,
    fetchNextPage: picker.onEndReached,
    itemCount: picker.options.length,
  });

  return (
    <Listbox
      dark={isDark}
      mode={mode}
      items={picker.options}
      onChange={onChange}
      identifiers={{
        key: 'value',
        value: 'value',
        label: 'label',
        children: 'options',
        image: 'image',
      }}
      selected={mode === 'multiple' ? value : undefined}
      onClose={onClose}
      onSearch={picker.onSearch}
      serverSearch
      noSort
      listFooter={
        <div data-testid={footerTestId}>
          {/*
           * The height is load-bearing: `useInfiniteScrollSentinel` observes with
           * `threshold: 0.1`, and a zero-height element has an intersection ratio of 0,
           * so it never reports as intersecting and no further page is ever fetched.
           */}
          <div ref={sentinelRef} className="h-4" aria-hidden />
          {picker.isLoadingMore && (
            <Typography
              fontSize={FontSizeEnum.Xs}
              color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
              className="block px-size-12 py-size-8"
            >
              {loadingMoreLabel}
            </Typography>
          )}
          <OptionsFooter
            isDark={isDark}
            isFetching={picker.isFetching}
            isError={picker.isError}
            isEmpty={picker.options.length === 0}
          />
        </div>
      }
    />
  );
}
